import { useEffect, useRef, useState } from 'react'
import { Check, ClipboardList, Loader2, Plus, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { US_STATES } from '@/lib/us-states'
import {
  useAddRegistrationItem,
  useCreateDealRegistration,
  useDealRegistration,
  useDeleteRegistrationItem,
  usePatchRegistration,
  usePatchRegistrationItem,
} from '@/features/documents/hooks'
import type { RegistrationPatchBody } from '@/features/documents/api'
import {
  ALL_ITEM_TYPES,
  REGISTRATION_ITEM_STATUS_LABELS,
  REGISTRATION_ITEM_TYPE_LABELS,
  REGISTRATION_STATUS_LABELS,
  type Registration,
  type RegistrationItemStatus,
  type RegistrationItemType,
  type RegistrationStatus,
} from '@/types/documents'

const ITEM_STATUS_ORDER: RegistrationItemStatus[] = [
  'pending',
  'in_progress',
  'submitted',
  'completed',
  'not_applicable',
]
const REG_STATUS_ORDER: RegistrationStatus[] = ['preparing', 'submitted', 'registered', 'completed']

const toISODate = (value?: string | null) => (value ? value.slice(0, 10) : '')
const daysUntil = (iso?: string | null) => {
  if (!iso) return null
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

/**
 * RegistrationCard is the post-sale title/registration workflow on the deal
 * detail page. It is created automatically when the deal is won (with a
 * boat); every checklist item is one filing the office must complete before
 * the customer's permanent registration lands.
 */
export function RegistrationCard({ dealId }: { dealId: string }) {
  const { data: registration, isLoading, isError } = useDealRegistration(dealId)
  const createMutation = useCreateDealRegistration(dealId)
  const patchMutation = usePatchRegistration({ dealId })
  const addItemMutation = useAddRegistrationItem(registration?.id ?? '', dealId)
  const patchItemMutation = usePatchRegistrationItem(registration?.id ?? '', dealId)
  const deleteItemMutation = useDeleteRegistrationItem(registration?.id ?? '', dealId)

  // Save feedback lives here, not in the fields form, because the form
  // remounts (keyed by updated_at) after every successful save.
  const [saveState, setSaveState] = useState<'idle' | 'saved' | 'error'>('idle')
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flashSaveState = (state: 'saved' | 'error') => {
    setSaveState(state)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(
      () => setSaveState('idle'),
      state === 'saved' ? 2500 : 6000,
    )
  }

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    },
    [],
  )

  const saveHeader = (body: RegistrationPatchBody) => {
    if (!registration) return
    patchMutation.mutate(
      { id: registration.id, body },
      {
        onSuccess: () => flashSaveState('saved'),
        onError: () => flashSaveState('error'),
      },
    )
  }

  const [newItemType, setNewItemType] = useState<RegistrationItemType>('other')
  const [newItemLabel, setNewItemLabel] = useState('')

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading registration…
        </CardContent>
      </Card>
    )
  }

  if (isError || !registration) {
    return (
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-4 w-4" /> Registration
            </CardTitle>
            <CardDescription>
              Post-sale title &amp; registration tracking. Winning a deal with a boat starts this
              workflow automatically.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Start registration
          </Button>
        </CardHeader>
        <CardContent>
          <p className="py-4 text-center text-sm text-muted-foreground">
            No registration started for this deal yet.
          </p>
        </CardContent>
      </Card>
    )
  }

  const doneCount = registration.items.filter(
    (i) => i.status === 'completed' || i.status === 'not_applicable',
  ).length

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4" /> Registration
            <Badge variant="outline">
              {doneCount}/{registration.items.length} done
            </Badge>
          </CardTitle>
          <CardDescription>
            Title &amp; registration filings for this sale. The registration auto-completes when
            every item is done or N/A.
          </CardDescription>
        </div>
        <Select
          value={registration.status}
          onValueChange={(value) =>
            patchMutation.mutate({ id: registration.id, body: { status: value as RegistrationStatus } })
          }
        >
          <SelectTrigger className="h-9 w-[150px]" aria-label="Registration status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REG_STATUS_ORDER.map((s) => (
              <SelectItem key={s} value={s}>
                {REGISTRATION_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Keyed by updated_at so local edits reset to server truth after
            each save / refetch, without a sync effect. */}
        <RegistrationHeaderFields
          key={registration.updated_at}
          registration={registration}
          pending={patchMutation.isPending}
          saveState={saveState}
          onSave={saveHeader}
        />

        <div className="space-y-1.5">
          <ul className="divide-y rounded-md border">
            {registration.items.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{item.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {REGISTRATION_ITEM_TYPE_LABELS[item.item_type]}
                  </div>
                </div>
                <Input
                  type="date"
                  value={toISODate(item.due_date)}
                  className="h-8 w-[140px] text-xs"
                  aria-label={`Due date for ${item.label}`}
                  onChange={(e) =>
                    patchItemMutation.mutate({
                      itemId: item.id,
                      body: { due_date: e.target.value },
                    })
                  }
                />
                <Select
                  value={item.status}
                  onValueChange={(value) =>
                    patchItemMutation.mutate({
                      itemId: item.id,
                      body: { status: value as RegistrationItemStatus },
                    })
                  }
                >
                  <SelectTrigger className="h-8 w-[140px] text-xs" aria-label={`Status of ${item.label}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ITEM_STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>
                        {REGISTRATION_ITEM_STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  aria-label={`Remove ${item.label}`}
                  onClick={() => deleteItemMutation.mutate(item.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <Select value={newItemType} onValueChange={(v) => setNewItemType(v as RegistrationItemType)}>
              <SelectTrigger className="h-8 w-[170px] text-xs" aria-label="New item type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALL_ITEM_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {REGISTRATION_ITEM_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={newItemLabel}
              placeholder="Item label (e.g. Trade-in title transfer)"
              className="h-8 flex-1 text-xs"
              onChange={(e) => setNewItemLabel(e.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={!newItemLabel.trim() || addItemMutation.isPending}
              onClick={() =>
                addItemMutation.mutate(
                  { item_type: newItemType, label: newItemLabel.trim() },
                  { onSuccess: () => setNewItemLabel('') },
                )
              }
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// RegistrationHeaderFields edits state / temp-tag deadline / lienholder /
// notes locally and saves explicitly, so tabbing through fields doesn't fire
// a request per keystroke.
const NO_STATE = '__none__' // Radix Select item values can't be an empty string

function RegistrationHeaderFields({
  registration,
  pending,
  saveState,
  onSave,
}: {
  registration: Registration
  pending: boolean
  saveState: 'idle' | 'saved' | 'error'
  onSave: (body: RegistrationPatchBody) => void
}) {
  const [state, setState] = useState(registration.state)
  const [tempTag, setTempTag] = useState(toISODate(registration.temp_tag_expires_at))
  const [lienholder, setLienholder] = useState(registration.lienholder_name)
  const [notes, setNotes] = useState(registration.notes)

  const tempTagDays = daysUntil(registration.temp_tag_expires_at)
  const tempTagUrgent = tempTagDays !== null && tempTagDays <= 14 && registration.status !== 'completed'
  const dirty =
    state !== registration.state ||
    tempTag !== toISODate(registration.temp_tag_expires_at) ||
    lienholder !== registration.lienholder_name ||
    notes !== registration.notes

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">State</Label>
          <Select
            value={state || NO_STATE}
            onValueChange={(value) => setState(value === NO_STATE ? '' : value)}
          >
            <SelectTrigger aria-label="Registration state">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_STATE}>(None)</SelectItem>
              {US_STATES.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reg-temptag" className="text-xs text-muted-foreground">
            Temp tag expires
            {tempTagUrgent && (
              <span className="ml-1.5 font-medium text-destructive">
                {tempTagDays !== null && tempTagDays < 0
                  ? '· expired'
                  : `· ${tempTagDays} day${tempTagDays === 1 ? '' : 's'} left`}
              </span>
            )}
          </Label>
          <Input
            id="reg-temptag"
            type="date"
            value={tempTag}
            className={tempTagUrgent ? 'border-destructive' : ''}
            onChange={(e) => setTempTag(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Last day the customer's temporary tag is valid. Highlights when within 14 days of
            expiry.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reg-lienholder" className="text-xs text-muted-foreground">
            Lienholder
          </Label>
          <Input
            id="reg-lienholder"
            value={lienholder}
            placeholder="Lender (if financed)"
            onChange={(e) => setLienholder(e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="reg-notes" className="text-xs text-muted-foreground">
            Notes
          </Label>
          <Input
            id="reg-notes"
            value={notes}
            placeholder="Filing office, confirmation numbers…"
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Button
            size="sm"
            disabled={pending || !dirty}
            onClick={() =>
              onSave({
                state,
                lienholder_name: lienholder,
                notes,
                temp_tag_expires_at: tempTag || undefined,
                clear_temp_tag: !tempTag,
              })
            }
          >
            {pending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : saveState === 'saved' ? (
              <Check className="mr-1.5 h-3.5 w-3.5" />
            ) : null}
            {saveState === 'saved' ? 'Saved' : 'Save'}
          </Button>
          {saveState === 'error' && (
            <p className="text-xs text-destructive">Save failed — try again.</p>
          )}
        </div>
      </div>
    </div>
  )
}
